// Loads curated comparisons from the database (comparison_categories +
// comparison_entries) and shapes them into the same ToolComparison[] structure
// that components used to import from src/data/comparisons.ts. This keeps the
// existing UI untouched while letting admins edit pros/cons/features from
// /admin/tools without code changes.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ToolComparison, ComparisonTool } from "@/data/comparisons";

interface DbCategory {
  id: string;
  category: string;
  description: string;
  display_order: number;
  is_active: boolean;
}

interface DbEntry {
  id: string;
  category_id: string;
  name: string;
  url: string;
  pricing: string;
  monthly_price: number | null;
  free_tier: boolean;
  rating: number;
  best_for: string;
  pros: unknown;
  cons: unknown;
  features: unknown;
  display_order: number;
}

const toStrArr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const toFeatures = (v: unknown): Record<string, boolean | string> => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out: Record<string, boolean | string> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === "boolean" || typeof val === "string") out[k] = val;
  }
  return out;
};

export function useComparisons(opts?: { includeInactive?: boolean }) {
  const includeInactive = !!opts?.includeInactive;
  const [comparisons, setComparisons] = useState<ToolComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    let catQuery = supabase
      .from("comparison_categories")
      .select("*")
      .order("display_order", { ascending: true });
    if (!includeInactive) catQuery = catQuery.eq("is_active", true);

    const [catRes, entryRes] = await Promise.all([
      catQuery,
      supabase
        .from("comparison_entries")
        .select("*")
        .order("display_order", { ascending: true }),
    ]);

    if (catRes.error || entryRes.error) {
      setError((catRes.error || entryRes.error)?.message || "Failed to load");
      setComparisons([]);
      setLoading(false);
      return;
    }

    const cats = (catRes.data ?? []) as DbCategory[];
    const entries = (entryRes.data ?? []) as DbEntry[];
    const byCat = new Map<string, DbEntry[]>();
    entries.forEach((e) => {
      const arr = byCat.get(e.category_id) ?? [];
      arr.push(e);
      byCat.set(e.category_id, arr);
    });

    const shaped: ToolComparison[] = cats.map((c) => ({
      category: c.category,
      description: c.description,
      tools: (byCat.get(c.id) ?? []).map<ComparisonTool>((e) => ({
        name: e.name,
        url: e.url,
        pricing: e.pricing,
        monthlyPrice:
          e.monthly_price === null ? null : Number(e.monthly_price),
        freeTier: e.free_tier,
        rating: Number(e.rating),
        bestFor: e.best_for,
        pros: toStrArr(e.pros),
        cons: toStrArr(e.cons),
        features: toFeatures(e.features),
      })),
    }));

    setComparisons(shaped);
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  return { comparisons, loading, error, reload };
}
